import React, { useEffect, useState } from 'react';
import {
  Box, Card, CardContent, Typography, Button, Stack, Chip, IconButton, Paper,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField, MenuItem, CircularProgress,
} from '@mui/material';
import { ViewKanban, Add, Delete, DragIndicator } from '@mui/icons-material';
import { api } from '../config/api';

interface Etapa { id: number; nombre: string; color: string; }
interface CardT { id: number; etapa_id: number; titulo: string; telefono?: string; nota?: string; valor?: number; }

const Pipeline: React.FC = () => {
  const [etapas, setEtapas] = useState<Etapa[]>([]);
  const [cards, setCards] = useState<CardT[]>([]);
  const [loading, setLoading] = useState(true);
  const [drag, setDrag] = useState<number | null>(null);
  const [dlg, setDlg] = useState(false);
  const [form, setForm] = useState<any>({ etapa_id: '', titulo: '', telefono: '', nota: '' });

  const cargar = () => {
    setLoading(true);
    api.get('/pipeline').then((r) => { setEtapas(r.data.etapas); setCards(r.data.cards); }).finally(() => setLoading(false));
  };
  useEffect(cargar, []);

  const onDrop = async (etapaId: number) => {
    if (drag == null) return;
    const id = drag; setDrag(null);
    setCards((cs) => cs.map((c) => (c.id === id ? { ...c, etapa_id: etapaId } : c)));
    await api.put(`/pipeline/cards/${id}/mover`, { etapa_id: etapaId });
  };

  const crearCard = async () => {
    const { data } = await api.post('/pipeline/cards', form);
    setCards((cs) => [...cs, data.card]); setDlg(false); setForm({ etapa_id: '', titulo: '', telefono: '', nota: '' });
  };

  const borrarCard = async (id: number) => {
    setCards((cs) => cs.filter((c) => c.id !== id));
    await api.delete(`/pipeline/cards/${id}`);
  };

  const agregarEtapa = async () => {
    const { data } = await api.post('/pipeline/etapas', { nombre: 'Nueva etapa' });
    setEtapas((e) => [...e, data.etapa]);
  };

  if (loading) return <Box sx={{ textAlign: 'center', mt: 6 }}><CircularProgress /></Box>;

  return (
    <Box>
      <Stack direction={{ xs: 'column', sm: 'row' }} alignItems={{ xs: 'flex-start', sm: 'center' }} justifyContent="space-between" spacing={1} sx={{ mb: 1 }}>
        <Stack direction="row" alignItems="center" spacing={1}>
          <ViewKanban color="primary" />
          <Typography variant="h4">Mi Pipeline</Typography>
        </Stack>
        <Stack direction="row" spacing={1}>
          <Button variant="outlined" startIcon={<Add />} onClick={agregarEtapa}>Etapa</Button>
          <Button variant="contained" startIcon={<Add />} onClick={() => { setForm({ ...form, etapa_id: etapas[0]?.id }); setDlg(true); }}>Tarjeta</Button>
        </Stack>
      </Stack>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        Arma tu embudo de ventas. Los leads que capture tu agente aparecen aquí automáticamente. Arrástralos entre etapas.
      </Typography>

      <Box sx={{ display: 'flex', gap: 2, overflowX: 'auto', pb: 2 }}>
        {etapas.map((etapa) => {
          const cs = cards.filter((c) => c.etapa_id === etapa.id);
          return (
            <Paper key={etapa.id}
              onDragOver={(e) => e.preventDefault()}
              onDrop={() => onDrop(etapa.id)}
              sx={{ minWidth: 280, width: 280, p: 1.5, bgcolor: '#f8fafc', flexShrink: 0, borderTop: `3px solid ${etapa.color}` }}>
              <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1.5 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>{etapa.nombre}</Typography>
                <Chip size="small" label={cs.length} sx={{ bgcolor: `${etapa.color}22`, color: etapa.color, fontWeight: 700 }} />
              </Stack>
              <Stack spacing={1}>
                {cs.map((c) => (
                  <Card key={c.id} draggable onDragStart={() => setDrag(c.id)}
                    sx={{ cursor: 'grab', '&:active': { cursor: 'grabbing' }, border: '1px solid #e2e8f0' }}>
                    <CardContent sx={{ p: '12px !important' }}>
                      <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                        <Stack direction="row" spacing={0.5} alignItems="center">
                          <DragIndicator sx={{ color: '#cbd5e1', fontSize: 18 }} />
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>{c.titulo}</Typography>
                        </Stack>
                        <IconButton size="small" onClick={() => borrarCard(c.id)}><Delete sx={{ fontSize: 16 }} /></IconButton>
                      </Stack>
                      {c.telefono && <Typography variant="caption" color="text.secondary" sx={{ display: 'block', ml: 3 }}>📱 {c.telefono}</Typography>}
                      {c.nota && <Typography variant="caption" color="text.secondary" sx={{ display: 'block', ml: 3 }}>{c.nota}</Typography>}
                    </CardContent>
                  </Card>
                ))}
                {cs.length === 0 && <Typography variant="caption" color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>Arrastra tarjetas aquí</Typography>}
              </Stack>
            </Paper>
          );
        })}
      </Box>

      <Dialog open={dlg} onClose={() => setDlg(false)} fullWidth maxWidth="xs">
        <DialogTitle>Nueva tarjeta</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField select label="Etapa" fullWidth value={form.etapa_id} onChange={(e) => setForm({ ...form, etapa_id: e.target.value })}>
              {etapas.map((e) => <MenuItem key={e.id} value={e.id}>{e.nombre}</MenuItem>)}
            </TextField>
            <TextField label="Título / Nombre" fullWidth value={form.titulo} onChange={(e) => setForm({ ...form, titulo: e.target.value })} />
            <TextField label="Teléfono" fullWidth value={form.telefono} onChange={(e) => setForm({ ...form, telefono: e.target.value })} />
            <TextField label="Nota" fullWidth multiline rows={2} value={form.nota} onChange={(e) => setForm({ ...form, nota: e.target.value })} />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDlg(false)}>Cancelar</Button>
          <Button variant="contained" disabled={!form.titulo || !form.etapa_id} onClick={crearCard}>Crear</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Pipeline;
